'use client';

import { useEffect, useRef } from 'react';
import { signIn, useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';

type GoogleOneTapCredentialResponse = { credential?: string };

type GoogleOneTapPromptProps = {
  googleClientId?: string;
  enabled?: boolean;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: Record<string, unknown>) => void;
          prompt: () => void;
          cancel: () => void;
        };
      };
    };
  }
}

const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';
const SCRIPT_ID = 'google-one-tap-gsi';

export const isOneTapEligible = (status: string, hasClientId: boolean, enabled = true) =>
  enabled && status === 'unauthenticated' && hasClientId;

const ONE_TAP_ROUTE_PREFIXES = [
  '/ativo',
  '/landing/',
  '/carteira',
  '/ferramentas',
  '/copiloto',
  '/renda-fixa',
  '/comparador',
  '/precos',
  '/funcionalidades',
  '/curso',
  '/relatorios-ia',
];

const isOneTapRoute = (pathname: string) =>
  pathname === '/' || ONE_TAP_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export function GoogleOneTapPrompt({ googleClientId, enabled = true }: GoogleOneTapPromptProps) {
  const pathname = usePathname();
  const { status } = useSession();
  const cancelTimeoutRef = useRef<number | null>(null);
  const promptActiveRef = useRef(false);

  useEffect(() => {
    const isEligible =
      isOneTapEligible(status, Boolean(googleClientId || process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID), enabled) &&
      Boolean(pathname && isOneTapRoute(pathname));

    if (!isEligible) return;

    if (cancelTimeoutRef.current !== null) {
      window.clearTimeout(cancelTimeoutRef.current);
      cancelTimeoutRef.current = null;
    }

    const clientId = googleClientId || process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

    const setupOneTap = () => {
      if (!window.google?.accounts?.id) {
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        use_fedcm_for_prompt: true,
        callback: async (_response: GoogleOneTapCredentialResponse) => {
          promptActiveRef.current = false;
          await signIn('google', { callbackUrl: window.location.href });
        },
      });

      // A re-run of this effect (React replaying effects in development, or
      // navigation between eligible routes) must not start a second FedCM
      // request while one is outstanding — the browser rejects it with
      // NotAllowedError. The active prompt keeps working and keeps this
      // component's callback.
      if (promptActiveRef.current) {
        return;
      }

      promptActiveRef.current = true;
      window.google.accounts.id.prompt();
    };

    const schedulePromptCancellation = () => {
      if (!promptActiveRef.current || cancelTimeoutRef.current !== null) {
        return;
      }

      // React replays effects in development. Delaying cancellation lets the
      // replacement effect reuse the prompt instead of aborting FedCM between
      // the two setup passes and logging an AbortError in the console.
      cancelTimeoutRef.current = window.setTimeout(() => {
        cancelTimeoutRef.current = null;
        if (!promptActiveRef.current) return;

        promptActiveRef.current = false;
        window.google?.accounts?.id?.cancel();
      }, 0);
    };

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.google?.accounts?.id) {
        setupOneTap();
      } else {
        existing.addEventListener('load', setupOneTap, { once: true });
      }
      return () => {
        existing.removeEventListener('load', setupOneTap);
        schedulePromptCancellation();
      };
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = GOOGLE_GSI_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', setupOneTap, { once: true });
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', setupOneTap);
      schedulePromptCancellation();
    };
  }, [enabled, googleClientId, pathname, status]);

  return null;
}
